/* eslint-disable react/no-danger */
/* eslint-disable no-underscore-dangle */
/* eslint-disable camelcase */
import React, { useState, useEffect, useRef, useMemo, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal, Popup } from 'semantic-ui-react';
import ReCAPTCHA from 'react-google-recaptcha';
import PropTypes from 'prop-types';

import { toggleESignModal, togglePaymentModal, updateFieldError, updateVisibleCaptcha } from '../../form/reducer/form';
import { doSaveFormForLater, FormUtils, validatePresentPage } from '../../form/utils';
import { SUBMIT_BUTTON_ID } from '../../builder/components/modals/logic-config/utils';
import FormTimeTriggeredSubmission from './form-time-triggered-submission';
import { defaultUserCustomTexts, toHex } from '../../../shared/form-lib';
import { formDesignProps } from '../../form_fields/ctrl_proptype';
import { FormContext } from '../../shared/contexts/form-context';
import FormPartialSubmission from './form-partial-submission';
import { useFormNetwork } from '../../../hooks/form-network';
import FormFullSubmission from './form-full-submission';
import { selectFormRules } from '../../form/selectors';
import FormSubmitError from './form-submit-error';
import ProgressBar from '../progressbar';
import {
  BtnWrapper,
  SubmitButton,
  LoadingLines,
  ButtonLoading,
  MainContainer,
  PrevNextButton,
  SubmissionText,
  PrevNextWrapper,
  LoadingContainer,
  ButtonsContainer,
  SubmissionMessage,
  SaveAndContinueButton,
  LoaderAndMessageContainer,
} from './form-pagination.styled';

const getBtnPostion = (position) => {
  if (position === 'center') return 'center';
  if (position === 'right') return 'flex-end';
  return 'flex-start';
};
export default function Pagination({
  form,
  timeUp,
  percent,
  progress,
  noOfPages,
  changePage,
  formDesign,
  enableTimer,
  presentPage,
  submitError,
  visiblePages,
  timerPurpose,
  errorMessage,
  currentPageNo,
  timerFinishDo,
  formSubmitted,
  setSubmitError,
  formBodyStyles,
  setErrorMessage,
  setFormSubmitted,
  submitInProgress,
  isPaymentCompleted,
  setSubmitInProgress,
  isSubmissionTriggered,
  shouldRequestEsignAgreement,
}) {
  const submitRef = useRef();
  const _reCaptchaRef = useRef();
  const dispatch = useDispatch();
  const { inForm } = useContext(FormContext);
  const saveAndContinue = form?.save_and_continue;
  const formUtils = useMemo(() => new FormUtils(), []);
  const userCustomTexts = form?.user_custom_texts || {};
  const globalForm = useSelector((state) => state.form);
  const responseStore = form?.response_store ?? 'formplus';
  const formRules = inForm && useSelector(selectFormRules);
  const _ = useFormNetwork(inForm, formSubmitted, submitError);

  const [email, setEmail] = useState(form.respondent_email || '');
  const [editPath, setEditPath] = useState('');
  const [viewLink, setViewLink] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [savedFormID, setSavedFormID] = useState(0);
  const [formResponses, setFormResponses] = useState([]);
  const [saveFormError, setSaveFormError] = useState(true);
  const [canSeeFormBack, setCanSeeFormBack] = useState(false);
  const [foundEmptyForm, setFoundEmptyForm] = useState(false);
  const [finalRenderedMessage, setFinalRenderedMessage] = useState('');
  const [validationInProgress, setValidationInProgress] = useState(false);
  const [fullSubmissionLoading, setFullSubmissionLoading] = useState(false);
  const [partialSubmissionLoading, setPartialSubmissionLoading] = useState(false);

  const {
    form_id,
    uploadCount,
    editResponse,
    hiddenFields,
    userIsOnline,
    closingPages,
    visibleCaptcha,
    formAllowsOffline,
    phoneNumberFields,
    isPayOnSubmission,
    hideSubmitButtton,
    respondentQuizTimerInfo,
    hasOfflineRestrictedRequiredField,
  } = globalForm;

  const getPriorityClosingPage = () => {
    if (!inForm) return;

    const isNotDefaultPage = (page) => page.id !== 'default';
    const isPageInHiddenFields = (page) => page.id in hiddenFields;
    const isPageVisible = (page) => !hiddenFields[page.id];
    const isPageTriggeredByFormRules = (page) =>
      formRules.some((rule) => rule.actions.some((action) => action.id === page.id));

    const closingPagesTriggeredByLogic = Object.values(closingPages).filter(
      (page) =>
        isNotDefaultPage(page) && isPageInHiddenFields(page) && isPageTriggeredByFormRules(page) && isPageVisible(page)
    );

    if (closingPagesTriggeredByLogic.length === 0) {
      return closingPages.default;
    }

    return closingPagesTriggeredByLogic.reduce(
      (priorityPage, currentPage) => (currentPage.priority < priorityPage.priority ? currentPage : priorityPage),
      { priority: Infinity }
    );
  };

  const priorityClosingPage = getPriorityClosingPage();

  const {
    final_message: finalMessage,
    after_redirect_to: afterRedirectToLink,
    show_print_response: showPrintResponse,
    another_response_link: anotherResponseLink,
    post_submission_alignment: postSubmissionAlignment,
    post_submission_direction: postSubmissionDirection,
  } = priorityClosingPage || {};

  const hasFinalMessage = globalForm.form.form_options?.enable_closing_page && finalMessage ? true : false;

  let redirectLink = (afterRedirectToLink?.trim() || '').toLowerCase();
  if (redirectLink && !redirectLink.startsWith('http://') && !redirectLink.startsWith('https://')) {
    redirectLink = `http://${redirectLink}`;
  }

  const isQuizTimeUp = timeUp.quizEnd;
  const isEventTimeUp = timeUp.eventEnd;
  const eventFormNoSubmission = isEventTimeUp && timerFinishDo === 'do not submit';
  const isTimerEndForceSubmit = isQuizTimeUp || (isEventTimeUp && timerFinishDo === 'submit');

  const showVisibleCaptcha = visibleCaptcha.checkCaptcha && currentPageNo === visiblePages.length;
  const invalidCaptcha = showVisibleCaptcha && !visibleCaptcha.captchaResponse.length;

  const editingResponse = window.location.pathname.includes('/edit/');
  const onSavedFormPage = window.location.pathname.includes('/saved/');
  const editReviewForm = /\/form\/\d{1,}\/edit\/\d{1,}\?params=/.test(window.location.href);
  const isWorkflowForm = window.location !== window.parent.location && window.location.hash.indexOf('workflow') > -1;

  const isEditedByUser = window.location.search?.includes('user');
  const isPreviewingFormOnOtherPages = window.location.search?.includes('FPLS_PREVIEWING');
  const isPreviewingFormOnAnExternalPage = window.location.search?.includes('FPLS_PREVIEWING_EXT');

  // Auto submit
  useEffect(() => {
    if (isTimerEndForceSubmit || isSubmissionTriggered) {
      submitRef?.current?.click(); // Optional checks in case logic disables the submit on a timer form
    }
  }, [isTimerEndForceSubmit, isSubmissionTriggered]);

  const isValidForOfflineSubmission = () => {
    if (userIsOnline) return true;
    if (!formAllowsOffline || hasOfflineRestrictedRequiredField) return false;
    return true;
  };

  const setCaptchaUpdate = (payload) => {
    dispatch(updateVisibleCaptcha({ ...payload }));
  };

  const shouldDisableSubmitButton = () => {
    if (
      !inForm ||
      uploadCount > 0 ||
      submitInProgress ||
      validationInProgress ||
      isPreviewingFormOnOtherPages ||
      !isValidForOfflineSubmission() ||
      (noOfPages > 1 && currentPageNo < noOfPages && !timeUp.quizEnd && !timeUp.eventEnd)
    )
      return true;
  };

  const handleSubmitForm = async (e) => {
    e.preventDefault();

    if (!shouldDisableSubmitButton()) {
      const pageIndex = currentPageNo - 1;
      const validationObj = {
        skipChecks: isTimerEndForceSubmit,
        phoneNumberFields,
        isPayOnSubmission,
        userCustomTexts,
        setErrorMessage,
        formId: form_id,
        responseStore,
        hiddenFields,
        presentPage,
      };

      setValidationInProgress(true);
      const { atLeastOneWrong, ctrls } = await validatePresentPage(validationObj);

      // Update field errors and scroll to first invalid field
      if (ctrls.length) {
        const errorCtrls = [];
        ctrls.forEach((ct) => {
          if (ct?._validationError) {
            if (Array.isArray(ct._validationError)) {
              ct._validationError.forEach((arr) => {
                if (arr?.some((erx) => erx !== '')) errorCtrls.push(ct);
              });
            } else {
              errorCtrls.push(ct);
            }
          }
        });

        if (errorCtrls.length) {
          document.getElementById(`fpl-${errorCtrls[0].internal_id}`)?.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'nearest',
          });
        }

        dispatch(updateFieldError({ ctrls, pageIndex }));
      }

      setValidationInProgress(false);

      if (!atLeastOneWrong) {
        setErrorMessage('');

        if (!isPaymentCompleted) return dispatch(togglePaymentModal(true));
        if (shouldRequestEsignAgreement) return dispatch(toggleESignModal(true));

        setSubmitInProgress(true);

        formUtils.submitForm({
          setEditPath,
          enableTimer,
          timerPurpose,
          finalMessage,
          isEditedByUser,
          setSubmitError,
          hasFinalMessage,
          setErrorMessage,
          form: globalForm,
          setFormSubmitted,
          setCaptchaUpdate,
          setFormResponses,
          showPrintResponse,
          setFoundEmptyForm,
          setCanSeeFormBack,
          setSubmitInProgress,
          isTimerEndForceSubmit,
          setFinalRenderedMessage,
          respondentQuizTimerInfo,
          setLoading: setFullSubmissionLoading,
        });
      }
    }
  };

  const handleCopyLink = (e, link) => {
    e.preventDefault();
    navigator.clipboard.writeText(link);
  };

  const handleChangeCaptcha = (captchaResponse) => {
    dispatch(updateVisibleCaptcha({ captchaResponse }));
  };

  const showFilledForm = () => {
    setSubmitError('');
  };

  const handleSaveAndContinueForm = () => {
    setPartialSubmissionLoading(true);
    doSaveFormForLater({
      savedFormID,
      setSavedFormID,
      onSavedFormPage,
      setSaveFormError,
      form: globalForm,
      email: email.toLowerCase(),
      setLoading: setPartialSubmissionLoading,
    });
  };

  const handleClickSaveAndContinueButton = (e) => {
    if (!inForm) return;
    e.preventDefault();
    setOpenModal(true);

    // If on a saved form link or
    // If not on saved link but triggering the
    // save and continue after the first time
    if (onSavedFormPage || (!onSavedFormPage && (email || savedFormID))) {
      handleSaveAndContinueForm();
    }
  };

  const RenderSubmitButton = ({ children }) => {
    const style = {
      padding: '1.5em',
      borderRadius: '3px',
      border: '2px solid #ccc',
    };

    if (isPreviewingFormOnOtherPages && noOfPages && currentPageNo === noOfPages) {
      return (
        <Popup size="tiny" position="top right" style={style} on={['hover', 'click']} trigger={children}>
          <Popup.Header>{`Submissions are not allowed on form preview. ${isPreviewingFormOnAnExternalPage
            ? ' Login or go to your dashboard to get a full experience of the application.'
            : ''
            }`}</Popup.Header>
        </Popup>
      );
    }
    return children;
  };

  const printSubmitButtonText = () => {
    if (submitInProgress) return <ButtonLoading submit />;
    if (editingResponse) return userCustomTexts?.edit_submit_text || defaultUserCustomTexts.edit_submit_text;
    return formDesign.old.submission_button_text || userCustomTexts?.submit_text || defaultUserCustomTexts.submit_text;
  };

  const printSaveForLaterButtonText = () => {
    if (submitInProgress) return <ButtonLoading saveAndContinue />;
    return formDesign.old.save_continue_button_text || 'Save and Continue Later';
  };

  const buttonConfig = {
    pagination: {
      style: {
        '--btn-font': formDesign.old.form_font,
        '--btn-color': toHex(formDesign.design.secondary_button_color),
        '--btn-bg': toHex(formDesign.design.secondary_button_background_color),
        '--btn-radius': `${form.submission_button_type === 'squared' ? 5 : formDesign.design.button_border_radius}px`,
      },
      nextPageText: formDesign.old.next_button_text || userCustomTexts?.next_page || defaultUserCustomTexts.next_page,
      prevPageText:
        formDesign.old.previous_button_text || userCustomTexts?.previous_page || defaultUserCustomTexts.previous_page,
    },
    saveForLater: {
      text: printSaveForLaterButtonText(),
      style: {
        '--btn-color': toHex(formDesign.design.secondary_button_color),
        '--btn-bag': toHex(formDesign.design.secondary_button_background_color),
        '--btn-radius': `${form.submission_button_type === 'squared' ? 5 : formDesign.design.button_border_radius}px`,
      },
    },
    submit: {
      text: printSubmitButtonText(),
      style: {
        '--btn-color': toHex(formDesign.design.button_text_color),
        '--btn-bag': toHex(formDesign.old.submission_button_background),
        '--btn-radius': `${form.submission_button_type === 'squared' ? 5 : formDesign.design.button_border_radius}px`,
      },
    },
  };

  const showSubmitError = !formSubmitted && submitError;
  const shouldHideSubmitButton = hideSubmitButtton || form?.options?.hide_submit_button;
  const showSubmitButton = !shouldHideSubmitButton && !hiddenFields[SUBMIT_BUTTON_ID];
  const showFullSubmissionPostSubmissionPage = !isTimerEndForceSubmit && formSubmitted;
  const showSaveAndContinueButton = saveAndContinue && !editingResponse && (!enableTimer || (enableTimer && timerPurpose !== 'quiz'));
  const showTimerTriggeredPostSubmissionPage =
    (isTimerEndForceSubmit && formSubmitted) || ((foundEmptyForm || eventFormNoSubmission) && !formSubmitted);

  return (
    <MainContainer style={{ ...formBodyStyles, '--font-family': formDesign.old.form_font }}>
      <ProgressBar
        color="#808080"
        percent={percent}
        progressing={progress}
        formDesign={formDesign}
        formPages={visiblePages}
        currentPageNo={currentPageNo}
        userCustomTexts={userCustomTexts}
      />

      {showVisibleCaptcha && (
        <ReCAPTCHA
          ref={_reCaptchaRef}
          sitekey={window.RECAPTCHA_KEY}
          onChange={handleChangeCaptcha}
          style={{ display: 'inline-block' }}
        />
      )}

      <ButtonsContainer>
        {noOfPages > 1 && (
          <PrevNextWrapper style={{ '--nextprev-align': formDesign.old.multipage_buttons_position }}>
            {!(currentPageNo - 1 === 0) && (
              <PrevNextButton
                type="button"
                onClick={() => changePage(-1)}
                style={buttonConfig.pagination.style}
                disabled={currentPageNo === 1 && noOfPages > 1}>
                {buttonConfig.pagination.prevPageText}
              </PrevNextButton>
            )}

            {!(currentPageNo - 1 === noOfPages - 1) && (
              <PrevNextButton
                type="button"
                onClick={() => changePage(1)}
                style={buttonConfig.pagination.style}
                disabled={currentPageNo === noOfPages && noOfPages > 1}>
                {buttonConfig.pagination.nextPageText}
              </PrevNextButton>
            )}
          </PrevNextWrapper>
        )}

        {uploadCount > 0 && (
          <SubmissionMessage upload>
            <h5>{userCustomTexts?.upload_in_progress || defaultUserCustomTexts.upload_in_progress}</h5>
            <p>{userCustomTexts?.wait_for_upload || defaultUserCustomTexts.wait_for_upload}</p>
          </SubmissionMessage>
        )}

        {invalidCaptcha && (
          <SubmissionMessage error>
            <h5>{userCustomTexts?.resolve_captcha || defaultUserCustomTexts.resolve_captcha}</h5>
            <p>{userCustomTexts?.captcha_error || defaultUserCustomTexts.captcha_error}</p>
          </SubmissionMessage>
        )}

        {errorMessage && (
          <SubmissionMessage error>
            <h5>{userCustomTexts?.incomplete_form || defaultUserCustomTexts.incomplete_form}</h5>
            <p>{errorMessage}</p>
          </SubmissionMessage>
        )}

        {!isWorkflowForm && !editReviewForm && (
          <BtnWrapper
            style={{
              '--btn-align': getBtnPostion(formDesign.old.submission_button_position),
            }}>
            {showSaveAndContinueButton && (
              <SaveAndContinueButton
                style={buttonConfig.saveForLater.style}
                onClick={handleClickSaveAndContinueButton}
                disabled={uploadCount > 0 || submitInProgress || !userIsOnline}>
                {buttonConfig.saveForLater.text}
              </SaveAndContinueButton>
            )}

            {showSubmitButton && (
              <RenderSubmitButton>
                <SubmitButton
                  ref={submitRef}
                  onClick={handleSubmitForm}
                  style={buttonConfig.submit.style}
                  submitInProgress={submitInProgress}
                  id={shouldDisableSubmitButton() ? 'disable' : ''}>
                  {buttonConfig.submit.text}
                </SubmitButton>
              </RenderSubmitButton>
            )}
          </BtnWrapper>
        )}
      </ButtonsContainer>

      <Modal open={openModal} onClose={() => setOpenModal(false)}>
        <FormPartialSubmission
          email={email}
          viewLink={viewLink}
          setEmail={setEmail}
          formDesign={formDesign}
          savedFormID={savedFormID}
          setViewLink={setViewLink}
          setOpenModal={setOpenModal}
          handleCopyLink={handleCopyLink}
          onSavedFormPage={onSavedFormPage}
          userCustomTexts={userCustomTexts}
          partialSubmissionLoading={partialSubmissionLoading}
          handleSaveAndContinueForm={handleSaveAndContinueForm}
        />
      </Modal>

      {fullSubmissionLoading && (
        <LoadingContainer>
          <LoaderAndMessageContainer>
            <LoadingLines fullSub />
            <LoadingLines fullSub />
            <LoadingLines fullSub />
            <LoadingLines fullSub />
          </LoaderAndMessageContainer>
          <SubmissionText submitting>
            {userCustomTexts?.wait_for_submit || defaultUserCustomTexts.wait_for_submit}
          </SubmissionText>
        </LoadingContainer>
      )}

      {showFullSubmissionPostSubmissionPage && (
        <FormFullSubmission
          editPath={editPath}
          editResponse={editResponse}
          redirectLink={redirectLink}
          formSubmitted={formSubmitted}
          formResponses={formResponses}
          handleCopyLink={handleCopyLink}
          userCustomTexts={userCustomTexts}
          hasFinalMessage={hasFinalMessage}
          showPrintResponse={showPrintResponse}
          anotherResponseLink={anotherResponseLink}
          finalRenderedMessage={finalRenderedMessage}
          postSubmissionAlignment={postSubmissionAlignment}
          postSubmissionDirection={postSubmissionDirection}
        />
      )}

      {showTimerTriggeredPostSubmissionPage && (
        <FormTimeTriggeredSubmission
          isQuizTimeUp={isQuizTimeUp}
          isEventTimeUp={isEventTimeUp}
          foundEmptyForm={foundEmptyForm}
          eventFormNoSubmission={eventFormNoSubmission}
        />
      )}

      {/* TODO: ADD ERROR SCREEN/INDICATOR FOR SAVE AND CONTINUE using 'saveFormError' state */}
      {showSubmitError && (
        <FormSubmitError
          submitError={submitError}
          canSeeFormBack={canSeeFormBack}
          showFilledForm={showFilledForm}
          userCustomTexts={userCustomTexts}
          formFont={formDesign.old.form_font}
        />
      )}
    </MainContainer>
  );
}

Pagination.defaultProps = {
  percent: null,
  progress: false,
  timeUp: {
    quizEnd: false,
    eventEnd: false,
  },
  submitError: '',
  noOfPages: null,
  timerPurpose: '',
  timerFinishDo: '',
  errorMessage: null,
  enableTimer: false,
  currentPageNo: null,
  formSubmitted: false,
  changePage: () => null,
  submitInProgress: false,
  formBodyStyles: undefined,
  setSubmitError: () => null,
  setErrorMessage: () => null,
  setFormSubmitted: () => null,
  setSubmitInProgress: () => null,
};

Pagination.propTypes = {
  percent: PropTypes.number,
  progress: PropTypes.bool,
  enableTimer: PropTypes.bool,
  timeUp: PropTypes.shape({
    quizEnd: PropTypes.bool,
    eventEnd: PropTypes.bool,
  }),
  changePage: PropTypes.func,
  noOfPages: PropTypes.number,
  formSubmitted: PropTypes.bool,
  errorMessage: PropTypes.string,
  timerPurpose: PropTypes.string,
  setSubmitError: PropTypes.func,
  setErrorMessage: PropTypes.func,
  currentPageNo: PropTypes.number,
  timerFinishDo: PropTypes.string,
  setFormSubmitted: PropTypes.func,
  submitInProgress: PropTypes.bool,
  setSubmitInProgress: PropTypes.func,
  formBodyStyles: PropTypes.shape({
    padding: PropTypes.string,
    marginTop: PropTypes.number,
    marginBottom: PropTypes.number,
  }),
  presentPage: PropTypes.shape({
    internal_id: PropTypes.string,
    controls: PropTypes.array.isRequired,
  }).isRequired,
  isPaymentCompleted: PropTypes.bool.isRequired,
  isSubmissionTriggered: PropTypes.bool.isRequired,
  form: PropTypes.objectOf(PropTypes.any).isRequired,
  shouldRequestEsignAgreement: PropTypes.bool.isRequired,
  formDesign: PropTypes.shape(formDesignProps).isRequired,
  visiblePages: PropTypes.arrayOf(PropTypes.object).isRequired,
  submitError: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
};
